// components/Practice.tsx
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  RefreshCw,
  Eye,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  ListChecks,
} from "lucide-react";
import { getAuthToken } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface Question {
  id: string;
  question: string;
  options: string[];
  correctAnswer?: number;
  explanation?: string;
  category: string;
  language: string;
}

interface PracticeProps {
  endpoint: string;
  sessionType: string;
  title: string;
  defaultLanguage?: string;
  limit?: number;
  examMode?: boolean;
}

const apiURL = import.meta.env.VITE_API_URL || "http://localhost:7845";

export function Practice({
  endpoint,
  sessionType,
  title,
  defaultLanguage = "en",
  limit = 10,
  examMode = false,
}: PracticeProps) {
  const [language, setLanguage] = useState(defaultLanguage);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: number }>({});
  const [showAnswerMode, setShowAnswerMode] = useState(false);
  const [score, setScore] = useState(0);
  const [questionIds, setQuestionIds] = useState<string[]>([]);

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: questions, isLoading, refetch } = useQuery<Question[]>({
    queryKey: [endpoint, language, questionIds, limit, examMode],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("language", language);
      params.append("limit", limit.toString());
      params.append("exam", examMode ? "true" : "false");

      const payload: any = {};
      if (questionIds.length > 0) {
        payload.questionIds = questionIds;
      }

      const response = await fetch(
        `${apiURL}${endpoint}?${params.toString()}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Failed to fetch questions");

      const data = await response.json();

      if (questionIds.length === 0 && data?.length > 0) {
        setQuestionIds(data.map((q: Question) => q.id.toString()));
      }

      return data;
    },
  });

  const saveHistory = useMutation({
    mutationFn: async (data: any) => {
      const token = getAuthToken();
      const res = await fetch("/api/history", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to save practice history");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/history"] });
    },
  });

  const current = questions?.[currentIndex];
  const total = questions?.length || 0;

  const handleAnswer = (idx: number) => {
    if (answers[currentIndex] !== undefined) return;

    const newAnswers = { ...answers, [currentIndex]: idx };
    setAnswers(newAnswers);

    if (!examMode) {
      setShowAnswerMode(true);
      if (current && idx === current.correctAnswer) {
        setScore((s) => s + 1);
      }
    }

    if (currentIndex === total - 1) {
      const correctCount = Object.entries(newAnswers).reduce(
        (acc, [qIndex, ans]) => {
          const q = questions?.[+qIndex];
          return (
            acc +
            (q && q.correctAnswer !== undefined && ans === q.correctAnswer
              ? 1
              : 0)
          );
        },
        0
      );

      saveHistory.mutate({
        category: sessionType,
        language,
        totalQuestions: total,
        correctAnswers: correctCount,
        score: Math.round((correctCount / total) * 100),
        timeTaken: 15,
        sessionType,
        answers: newAnswers,
      });

      toast({
        title: "Practice Complete!",
        description: `You scored ${correctCount}/${total} (${Math.round(
          (correctCount / total) * 100
        )}%)`,
      });
    }
  };

  const optionClass = (isCorrect: boolean, idx: number) => {
    const answered = answers[currentIndex] !== undefined;
    const selected = answers[currentIndex] === idx;

    return cn(
      "w-full text-left px-4 py-3 rounded-lg border transition gcep-option-btn",
      "text-foreground",
      // default
      !answered && "bg-background",
      // correct
      answered && isCorrect && "border-gcep-success bg-green-100 text-gcep-success dark:bg-green-900",
      // wrong
      answered && selected && !isCorrect && "border-gcep-danger bg-red-100 text-gcep-danger dark:bg-red-900"
    );
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-64 mx-auto mb-4" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 flex items-center justify-center">
        <ListChecks className="text-[hsl(201,91%,60%)] mr-2" /> {title}
      </h2>

      {/* Controls */}
      <div className="flex justify-center gap-4 mb-6">
        <Select value={language} onValueChange={setLanguage}>
          <SelectTrigger className="w-40 bg-background text-foreground border border-border">
            <SelectValue placeholder="Select language" />
          </SelectTrigger>
          <SelectContent className="bg-background text-foreground border border-border">
            <SelectItem value="en">English</SelectItem>
            <SelectItem value="gu">Gujarati</SelectItem>
          </SelectContent>
        </Select>

        <Button
          onClick={() => {
            setAnswers({});
            setShowAnswerMode(false);
            setScore(0);
            setCurrentIndex(0);
            setQuestionIds([]);
            refetch();
          }}
          className="gcep-primary-btn"
        >
          <RefreshCw size={16} className="mr-2" /> New Questions
        </Button>

        {!examMode && (
          <Button
            onClick={() => {
              showAnswerMode && setAnswers({});
              setShowAnswerMode(!showAnswerMode);
            }}
            variant="secondary"
          >
            <Eye size={16} className="mr-2" />{" "}
            {showAnswerMode ? "Hide Answer" : "Show Answer"}
          </Button>
        )}
      </div>

      {/* Question */}
      {current ? (
        <Card className="gcep-card p-8">
          <div className="flex justify-between mb-6">
            <span>
              Question {currentIndex + 1} of {total}
            </span>
            {!examMode && (
              <span>
                Score: {score}/{total}
              </span>
            )}
          </div>

          <h3 className="text-xl font-semibold mb-6">{current.question}</h3>

          <div className="space-y-3 mb-6">
            {current.options.map((opt, idx) => {
              const isCorrect = current.correctAnswer === idx;

              return (
                <button
                  key={idx}
                  onClick={() => handleAnswer(idx)}
                  disabled={answers[currentIndex] !== undefined}
                  className={optionClass(isCorrect, idx)}
                >
                  {String.fromCharCode(65 + idx)}) {opt}
                </button>
              );
            })}
          </div>

          {/* Explanation */}
          {!examMode &&
            (answers[currentIndex] !== undefined || showAnswerMode) && (
              <div className="bg-accent border-l-4 border-gcep-primary p-4 rounded-lg mb-6">
                <h4 className="font-semibold flex items-center text-gcep-primary mb-2">
                  <Lightbulb size={18} className="mr-2" /> Explanation
                </h4>
                <p>
                  <strong>Correct Answer:</strong>{" "}
                  {String.fromCharCode(65 + (current.correctAnswer ?? 0))}{" "}
                  {current.options[current.correctAnswer ?? 0]}
                </p>
                <p>{current.explanation}</p>
              </div>
            )}

          {/* Navigation */}
          <div className="flex justify-between">
            <Button
              onClick={() => {
                setCurrentIndex((i) => Math.max(0, i - 1));
              }}
              disabled={currentIndex === 0}
              variant="secondary"
            >
              <ChevronLeft size={16} className="mr-2" /> Previous
            </Button>
            <Button
              onClick={() => {
                setShowAnswerMode(false);
                setCurrentIndex((i) => Math.min(total - 1, i + 1));
              }}
              disabled={currentIndex === total - 1}
              className="gcep-primary-btn"
            >
              Next <ChevronRight size={16} className="ml-2" />
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="p-8 text-center">No questions available</Card>
      )}
    </div>
  );
}
